const MapModule = {
  map: null,
  infowindow: null,
  markers: [],
  markerData: [],
  /** Map station id → Leaflet marker */
  markerByStationId: {},
  layers: {},
  _floatingPopup: null,
  _isReady: false,
  _readyResolve: null,
  _readyPromise: null,

  _normalizeStationId(id) {
    return id == null ? "" : String(id);
  },

  _hasValidCoords(lat, lng) {
    return (
      !isNaN(lat) &&
      !isNaN(lng) &&
      lat !== 0 &&
      lng !== 0 &&
      Math.abs(lat) <= 90 &&
      Math.abs(lng) <= 180
    );
  },

  _getMapboxConfig() {
    const cfg = DashboardConfig.map || {};
    const server = typeof DashboardServerConfig !== "undefined" ? DashboardServerConfig : {};
    return {
      accessToken: server.mapboxAccessToken || cfg.accessToken || "",
      streetsStyle: server.mapboxStreetsStyle || cfg.streetsStyle || "mapbox://styles/mapbox/streets-v11",
      satelliteStyle:
        server.mapboxSatelliteStyle ||
        cfg.satelliteStyle ||
        "mapbox://styles/mapbox/satellite-streets-v12",
      appName: server.organizationName || "Higertech",
    };
  },

  _getAttribution(appName) {
    return `&copy;${new Date().getFullYear()} ${appName}`;
  },

  /**
   * Lightweight InfoWindow-compatible facade for Leaflet popups
   */
  _createInfoWindowFacade() {
    const self = this;
    return {
      _content: "",
      close() {
        if (self.map) {
          self.map.closePopup();
        }
        if (self._floatingPopup && self.map) {
          self.map.removeLayer(self._floatingPopup);
          self._floatingPopup = null;
        }
      },
      setContent(html) {
        this._content = html;
      },
      open({ anchor, map, position } = {}) {
        const content = this._content;
        const infoWindowCfg =
          (DashboardConfig.map && DashboardConfig.map.infoWindow) || {};
        const popupOptions = {
          maxWidth: infoWindowCfg.maxWidth || 400,
          minWidth: infoWindowCfg.minWidth || 280,
          className: "dashboard-map-popup",
          autoPan: true,
          keepInView: true,
        };

        if (anchor && typeof anchor.bindPopup === "function") {
          anchor.unbindPopup();
          anchor.bindPopup(content, popupOptions);
          anchor.openPopup();
          return;
        }

        if (map && position) {
          if (self._floatingPopup) {
            map.removeLayer(self._floatingPopup);
          }
          self._floatingPopup = L.popup(popupOptions)
            .setLatLng([position.lat, position.lng])
            .setContent(content)
            .openOn(map);
        }
      },
    };
  },

  /**
   * Ensure "Lihat Detail" follows href (/Station/Detail/{slug}).
   * Leaflet can swallow popup clicks — disable map propagation only.
   */
  _bindPopupDetailHandlers(station) {
    const detailLinks = document.querySelectorAll(
      '.leaflet-popup-content a.btn-primary[href*="/Station/Detail/"], .leaflet-popup-content a.btn-primary[href*="/station/detail/"]',
    );

    detailLinks.forEach((link) => {
      if (typeof L !== "undefined" && L.DomEvent) {
        L.DomEvent.disableClickPropagation(link);
      }

      // Fallback if href/slug missing in generated HTML
      link.addEventListener("click", (e) => {
        const href = (link.getAttribute("href") || "").trim();
        const slug = station && station.slug;
        if ((!href || href === "#" || href.endsWith("/Station/Detail/")) && slug) {
          e.preventDefault();
          window.open(`/Station/Detail/${slug}`, "_blank");
        }
      });
    });
  },

  _createRasterBasemaps(appName) {
    const attr = this._getAttribution(appName);
    return [
      {
        layer: L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}",
          { attribution: attr + " | Esri", maxZoom: 16 },
        ),
        icon: "/images/basemaps/basemap-grayscale.jpg",
        name: "Grayscale",
      },
      {
        layer: L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          { attribution: appName + " " + attr + " | Esri", maxZoom: 19 },
        ),
        icon: "/images/basemaps/basemap-satellite.jpg",
        name: "Satellite",
      },
      {
        layer: L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          { attribution: appName + " " + attr + " | Esri", maxZoom: 19 },
        ).addTo(this.map),
        icon: "/images/basemaps/world-imagery.jpg",
        name: "W.Imagery",
      },
    ];
  },

  _createMapboxBasemaps(cfg) {
    const appName = cfg.appName;
    const attr = this._getAttribution(appName);
    return [
      {
        layer: L.mapboxGL({
          accessToken: cfg.accessToken,
          style: cfg.streetsStyle,
          attribution: attr + " | Mapbox",
        }),
        icon: "/images/basemaps/basemap-grayscale.jpg",
        name: "Grayscale",
      },
      {
        layer: L.mapboxGL({
          accessToken: cfg.accessToken,
          style: cfg.satelliteStyle,
          attribution: appName + " " + attr + " | Mapbox",
        }),
        icon: "/images/basemaps/basemap-satellite.jpg",
        name: "Satellite",
      },
      {
        layer: L.tileLayer(
          "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          { attribution: appName + " " + attr },
        ).addTo(this.map),
        icon: "/images/basemaps/world-imagery.jpg",
        name: "W.Imagery",
      },
    ];
  },

  async _isMapboxTokenValid(token) {
    if (!token) return false;
    try {
      const res = await fetch(
        "https://api.mapbox.com/styles/v1/mapbox/streets-v11?access_token=" +
          encodeURIComponent(token),
        { method: "GET" },
      );
      return res.ok;
    } catch (err) {
      console.warn("Mapbox token check failed:", err);
      return false;
    }
  },

  async setupBasemaps() {
    const cfg = this._getMapboxConfig();
    const token = cfg.accessToken;
    const appName = cfg.appName;

    if (typeof L.basemapsSwitcher === "undefined") {
      console.warn("Basemap switcher unavailable, using OSM tiles.");
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap",
        maxZoom: 18,
      }).addTo(this.map);
      return;
    }

    const canUseMapbox =
      !!token &&
      typeof L.mapboxGL !== "undefined" &&
      (await this._isMapboxTokenValid(token));

    if (!canUseMapbox) {
      console.warn(
        "Mapbox token invalid/unavailable — using Esri/CARTO raster basemaps.",
      );
    }

    const basemaps = canUseMapbox
      ? this._createMapboxBasemaps(cfg)
      : this._createRasterBasemaps(appName);

    new L.basemapsSwitcher(basemaps, { position: "topright" }).addTo(this.map);
  },

  isReady() {
    return this._isReady && !!this.map && typeof L !== "undefined";
  },

  async init(config) {
    console.log("🗺️ MapModule.init called", config);

    this._readyPromise = new Promise((resolve) => {
      this._readyResolve = resolve;
    });

    try {
      await DashboardHelpers.waitForLeaflet();

      const lat = parseFloat(config.latitude) || -1.26916;
      const lng = parseFloat(config.longitude) || 116.825264;
      const zoom = parseInt(config.zoom, 10) || 5;
      const deferMarkers = !!config.deferMarkers;

      this.map = L.map("map", {
        center: [lat, lng],
        zoom,
        zoomControl: true,
      });
      this.map.attributionControl.setPrefix(false);
      await this.setupBasemaps();

      this.infowindow = this._createInfoWindowFacade();

      await this.loadLegend();

      if (!deferMarkers) {
        await this.fetchMarkers();
        await this.loadRiverLayers();
        await this.loadWatershedLayers();
      }

      this._isReady = true;
      console.log("✅ MapModule is fully ready");
    } catch (err) {
      this._isReady = false;
      console.error("Map init failed:", err);
      DashboardHelpers.showMapLoadError(
        "Peta tidak dapat dimuat. Data monitoring dan CCTV tetap tersedia.",
      );
      throw err;
    } finally {
      if (this._readyResolve) {
        this._readyResolve();
      }
    }
  },

  ready() {
    if (this._isReady) return Promise.resolve();
    if (!this._readyPromise) {
      this._readyPromise = new Promise((resolve) => {
        this._readyResolve = resolve;
      });
    }
    return this._readyPromise;
  },

  triggerResize() {
    if (!this.map) return;
    this.map.invalidateSize();
  },

  async fetchMarkers() {
    if (!this.isReady()) return [];

    try {
      const data = await DashboardAPI.getMarkers();
      this.clearMarkers();
      this.markerData = data || [];
      this.markerByStationId = {};
      await this.addMarkers(this.markerData);
      return this.markerData;
    } catch (error) {
      console.error("Error fetching markers:", error);
      throw error;
    }
  },

  async addMarkers(data) {
    if (!this.isReady() || !data) return;

    const coordMap = new Map();
    // Tiny fixed offset (~5–8 m) only for exact-duplicate coordinates —
    // never zoom-scaled degrees (that pushed markers into the ocean).
    const DUPLICATE_OFFSET_DEG = 0.00005;

    data.forEach((station) => {
      if (
        typeof StationMarkerConfig !== "undefined" &&
        !StationMarkerConfig.shouldShowMarkerFromData(station)
      ) {
        return;
      }

      if (!station.latitude || !station.longitude) return;

      const rawLat = parseFloat(station.latitude);
      const rawLng = parseFloat(station.longitude);
      if (!this._hasValidCoords(rawLat, rawLng)) return;

      const coordKey = `${rawLat.toFixed(6)},${rawLng.toFixed(6)}`;
      const occurrence = coordMap.get(coordKey) || 0;
      coordMap.set(coordKey, occurrence + 1);

      let finalLat = rawLat;
      let finalLng = rawLng;
      if (occurrence > 0) {
        const angle = (occurrence * 2.2) % (Math.PI * 2);
        finalLat += Math.sin(angle) * DUPLICATE_OFFSET_DEG * occurrence;
        finalLng += Math.cos(angle) * DUPLICATE_OFFSET_DEG * occurrence;
      }

      const markerEl = this.createMarkerElement(station);
      const size = 24;
      const icon = L.divIcon({
        className: "leaflet-div-icon station-leaflet-icon",
        html: markerEl.outerHTML,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
        popupAnchor: [0, -size / 2],
      });

      const marker = L.marker([finalLat, finalLng], {
        icon,
        keyboard: false,
        riseOnHover: true,
      }).addTo(this.map);
      marker.on("click", () => this.showInfoWindow(station, marker));

      this.markers.push(marker);
      this.markerByStationId[this._normalizeStationId(station.id)] = marker;
    });
  },

  createMarkerElement(station) {
    const customMarker = document.createElement("div");
    customMarker.className =
      typeof StationMarkerConfig !== "undefined"
        ? StationMarkerConfig.getMarkerClassFromData(station)
        : this.getLegacyMarkerClass(station);
    return customMarker;
  },

  getLegacyMarkerClass(station) {
    const customMarkerClass = document.createElement("div");

    switch (station.type) {
      case DashboardConstants.STATION_TYPES.AWLR:
      case DashboardConstants.STATION_TYPES.TMAS:
        customMarkerClass.className = this.getAwlrMarkerClass(
          parseInt(station.status, 10),
          DashboardConstants.STATION_TYPES.AWLR,
        );
        break;
      case DashboardConstants.STATION_TYPES.TMAT:
      case DashboardConstants.STATION_TYPES.AGWLR:
        customMarkerClass.className = this.getAwlrMarkerClass(
          parseInt(station.status, 10),
          DashboardConstants.STATION_TYPES.AGWLR,
        );
        break;
      case DashboardConstants.STATION_TYPES.OW:
        customMarkerClass.className = "ow-marker";
        break;
      case DashboardConstants.STATION_TYPES.ARR:
        customMarkerClass.className = "arr-marker";
        break;
      case DashboardConstants.STATION_TYPES.AWLR_ARR:
        customMarkerClass.className = `awlr-arr-marker ${this.getWarningClass(
          parseInt(station.status, 10),
        )}`;
        break;
      case DashboardConstants.STATION_TYPES.AWS:
        customMarkerClass.className = "aws-marker";
        break;
      case DashboardConstants.STATION_TYPES.FM:
        customMarkerClass.className = "fm-marker";
        break;
      case DashboardConstants.STATION_TYPES.WQ:
        customMarkerClass.className = "wq-marker";
        break;
      case DashboardConstants.STATION_TYPES.VNOTCH:
        customMarkerClass.className = "vnotch-marker";
        break;
      case DashboardConstants.STATION_TYPES.OSP:
        customMarkerClass.className = "osp-marker";
        break;
      case DashboardConstants.STATION_TYPES.AVWR:
        customMarkerClass.className = "avwr-marker";
        break;
      default:
        customMarkerClass.className = "default-marker";
    }

    return customMarkerClass.className;
  },

  getAwlrMarkerClass(status, type) {
    const baseClass =
      type === DashboardConstants.STATION_TYPES.AGWLR
        ? "agwlr-marker"
        : "awlr-marker";
    const statusMap = {
      0: "",
      1: "alert-siaga1",
      2: "alert-siaga2",
      3: "alert-siaga3",
    };
    return `${baseClass} ${statusMap[status] || ""}`.trim();
  },

  getWarningClass(status) {
    const statusMap = {
      0: "",
      1: "alert-siaga1",
      2: "alert-siaga2",
      3: "alert-siaga3",
    };
    return statusMap[status] || "";
  },

  showInfoWindow(station, marker) {
    if (this.infowindow) {
      this.infowindow.close();
    }

    const content = PopupModule.generate(station);
    this.infowindow.setContent(content);
    this.infowindow.open({
      anchor: marker,
      map: this.map,
    });

    setTimeout(() => this._bindPopupDetailHandlers(station), 50);
  },

  triggerStationDetail(station) {
    const stationType = String(station.type || "").toUpperCase();
    if (stationType === "AVWR") {
      if (station.slug) {
        window.open(`/Station/Detail/${station.slug}`, "_blank");
      }
      return;
    }

    const resolvedType =
      stationType === DashboardConstants.STATION_TYPES.TMAT
        ? DashboardConstants.STATION_TYPES.AGWLR
        : stationType;

    const tabMapping = {
      AWLR: "tab-awlr",
      TMAS: "tab-awlr",
      TMAT: "tab-tmat",
      AGWLR: "tab-tmat",
      OW: "tab-ow",
      ARR: "tab-arr",
      AWLR_ARR: "tab-awlr-arr",
      AWS: "tab-aws",
      FM: "tab-fm",
      WQ: "tab-wq",
      AVWR: "tab-avwr",
    };

    const tabId = tabMapping[resolvedType] || "tab-awlr";

    const tabTrigger = document.querySelector(`a[href="#${tabId}"]`);
    if (tabTrigger) {
      const tab = new bootstrap.Tab(tabTrigger);
      tab.show();
    }

    setTimeout(() => {
      let cardSelector = "";

      switch (resolvedType) {
        case "AGWLR":
          cardSelector = `#card-station-tmat-${station.id}`;
          break;
        case "AWLR":
        case "TMAS":
          cardSelector = `#card-station-awlr-${station.id}`;
          break;
        case "ARR":
          cardSelector = `#card-station-arr-${station.id}`;
          break;
        case "AWLR_ARR":
          cardSelector = `#card-station-awlr-arr-${station.id}`;
          break;
        case "AWS":
          cardSelector = `#card-station-aws-${station.id}`;
          break;
        case "FM":
          cardSelector = `#card-station-fm-${station.id}`;
          break;
        case "WQ":
          cardSelector = `#card-station-wq-${station.id}`;
          break;
        case "AVWR":
          cardSelector = `#card-station-avwr-${station.id}`;
          break;
      }

      const cardElement = document.querySelector(cardSelector);

      if (cardElement) {
        cardElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        setTimeout(() => {
          cardElement.click();
          this.infowindow.close();
        }, 500);
      } else {
        console.warn(`⚠️ Card not found: ${cardSelector}`);

        if (typeof StationModule !== "undefined") {
          const dummyElement = document.createElement("div");
          dummyElement.setAttribute("data-ts-id", station.id);
          dummyElement.setAttribute("data-name", station.name);
          dummyElement.setAttribute("data-device", station.deviceId);
          const detailType =
            resolvedType === DashboardConstants.STATION_TYPES.TMAT
              ? DashboardConstants.STATION_TYPES.AGWLR
              : resolvedType;
          dummyElement.setAttribute("data-type", detailType);

          if (
            detailType === "AWLR" ||
            detailType === "AGWLR" ||
            detailType === "TMAS"
          ) {
            dummyElement.setAttribute("data-station-type", detailType);
            dummyElement.setAttribute("data-siaga1", station.siaga1 || "");
            dummyElement.setAttribute("data-siaga2", station.siaga2 || "");
            dummyElement.setAttribute("data-siaga3", station.siaga3 || "");
          }

          StationModule.showDetail(dummyElement, new Event("click"));
          this.infowindow.close();
        } else {
          console.error("❌ StationModule not available");
        }
      }
    }, 300);
  },

  showMarkerPopup(stationId, fallbackStation) {
    if (
      typeof DashboardHelpers !== "undefined" &&
      typeof DashboardHelpers.scrollToDashboardMap === "function"
    ) {
      DashboardHelpers.scrollToDashboardMap();
    } else {
      const mapContainer = document.getElementById("map");
      if (mapContainer) {
        mapContainer.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }

    const scrollDelay =
      typeof DashboardHelpers !== "undefined" &&
      typeof DashboardHelpers.isMobileDashboard === "function" &&
      DashboardHelpers.isMobileDashboard()
        ? 550
        : 300;

    const id = this._normalizeStationId(stationId);
    let station = this.markerData.find(
      (s) => this._normalizeStationId(s.id) === id,
    );

    if (!station && fallbackStation) {
      station = {
        ...fallbackStation,
        id: fallbackStation.id ?? stationId,
        type: (fallbackStation.type || "").toUpperCase(),
      };
    }

    if (!station) {
      console.warn("⚠️ Station not found in markerData:", stationId);
      return false;
    }

    const lat = parseFloat(station.latitude);
    const lng = parseFloat(station.longitude);
    if (!this._hasValidCoords(lat, lng)) {
      return false;
    }

    const marker = this.markerByStationId[id];

    setTimeout(() => {
      this._openInfoWindowAtStation(station, marker || null);
    }, scrollDelay);

    return true;
  },

  focusOnStation(stationId) {
    const id = this._normalizeStationId(stationId);
    const station = this.markerData.find(
      (s) => this._normalizeStationId(s.id) === id,
    );

    if (!station) {
      console.warn("⚠️ Station not found:", stationId);
      return;
    }

    this.map.setView(
      [parseFloat(station.latitude), parseFloat(station.longitude)],
      14,
    );
  },

  toggleLayer(layerName, show) {
    const layer = this.layers[layerName];
    if (!layer || !this.map) return;

    if (show) {
      if (!this.map.hasLayer(layer)) layer.addTo(this.map);
    } else if (this.map.hasLayer(layer)) {
      this.map.removeLayer(layer);
    }
  },

  clearMarkers() {
    this.markers.forEach((marker) => {
      if (this.map && this.map.hasLayer(marker)) {
        this.map.removeLayer(marker);
      }
    });
    this.markers = [];
    this.markerByStationId = {};
  },

  _openInfoWindowAtStation(station, marker) {
    const lat = parseFloat(station.latitude);
    const lng = parseFloat(station.longitude);

    if (!this._hasValidCoords(lat, lng)) {
      return false;
    }

    if (this.infowindow) {
      this.infowindow.close();
    }

    const normalizedStation = {
      ...station,
      type: (station.type || "").toUpperCase(),
    };
    const content = PopupModule.generate(normalizedStation);
    this.infowindow.setContent(content);

    const currentZoom = this.map.getZoom();
    const targetZoom = currentZoom < 13 ? 13 : currentZoom;
    this.map.setView([lat, lng], targetZoom);

    this.infowindow.open(
      marker
        ? { anchor: marker, map: this.map }
        : { map: this.map, position: { lat, lng } },
    );

    setTimeout(() => this._bindPopupDetailHandlers(normalizedStation), 50);

    if (marker) {
      const el = marker.getElement();
      const bounceTarget =
        (el && el.querySelector('[class*="-marker"]')) || el;
      if (bounceTarget) {
        bounceTarget.classList.add("marker-bounce");
        setTimeout(() => {
          bounceTarget.classList.remove("marker-bounce");
        }, 2000);
      }
    }

    return true;
  },

  filterMarkers(types) {
    this.clearMarkers();
    const filteredData = this.markerData.filter((station) => {
      if (typeof StationMarkerConfig === "undefined") {
        return types.includes(station.type);
      }

      const markerType = StationMarkerConfig.resolveDataType(station.type);
      return types.includes(markerType) || types.includes(station.type);
    });
    this.addMarkers(filteredData);
  },

  async loadLegend() {
    try {
      const response = await DashboardAPI.get("/station/type/total");

      let legendHtml =
        '<div class="py-1 px-2" style="background-color: white;box-shadow: 0 0 15px rgba(0, 0, 0, 0.3); border-radius: 1px; margin-bottom:10px">';
      legendHtml += '<h6 class="fw-bold py-1 px-0 m-1">Tipe Pos</h6>';

      response.forEach((item) => {
        const legendEntry =
          typeof StationMarkerConfig !== "undefined"
            ? StationMarkerConfig.buildLegendEntry(item.type)
            : DashboardConstants.STATION_CONFIG[item.type]
              ? {
                  type: item.type,
                  name: DashboardConstants.STATION_CONFIG[item.type].name,
                  classMarker:
                    DashboardConstants.STATION_CONFIG[item.type].classMarker,
                  filterValue:
                    typeof StationMarkerConfig !== "undefined"
                      ? StationMarkerConfig.getFilterValue(item.type)
                      : item.type === "TMAT" || item.type === "AGWLR"
                        ? "AGWLR"
                        : item.type,
                }
              : null;

        if (legendEntry) {
          legendHtml += `
                        <div class="d-flex align-items-center mb-1 justify-content-between">
                            <div class="${legendEntry.classMarker}" style="width: 18px;height: 18px;box-shadow:none;"></div>
                            <div class="me-1" style="font-weight: bold;">${legendEntry.name}</div>
                            <input name="tipe-sensor" value="${legendEntry.filterValue}" type="checkbox" checked onchange="MapModule.handleMarkerFilter(this)" />
                        </div>
                    `;
        }
      });

      if (
        typeof StationMarkerConfig !== "undefined" &&
        typeof StationMarkerConfig.buildLegendFootnoteHtml === "function"
      ) {
        legendHtml += StationMarkerConfig.buildLegendFootnoteHtml();
      } else {
        legendHtml +=
          '<p class="mb-0 mt-2 text-muted small fw-semibold">T = Telemetri</p>';
      }
      legendHtml += "</div>";

      const LegendControl = L.Control.extend({
        options: { position: "bottomleft" },
        onAdd: function () {
          const div = L.DomUtil.create("div", "dashboard-map-legend");
          div.innerHTML = legendHtml;
          L.DomEvent.disableClickPropagation(div);
          L.DomEvent.disableScrollPropagation(div);
          return div;
        },
      });

      new LegendControl().addTo(this.map);
    } catch (error) {
      console.error("Error loading legend:", error);
    }
  },

  handleMarkerFilter(checkbox) {
    const checkedTypes = Array.from(
      document.querySelectorAll('input[name="tipe-sensor"]:checked'),
    ).map((cb) => cb.value);

    this.filterMarkers(checkedTypes);
  },

  async loadRiverLayers() {
    try {
      const rivers = await DashboardAPI.get("/Home/River/Files");

      rivers.forEach((river) => {
        this.loadRiverFile(river.fileName);
      });
    } catch (error) {
      console.error("Error loading river layers:", error);
    }
  },

  loadRiverFile(fileName) {
    const layerName = fileName.split(".")[0];
    const layerGroup = L.layerGroup();
    this.layers[layerName] = layerGroup;

    $.getJSON(
      `/data/rivers/${DashboardServerConfig.organizationCode}/${fileName}`,
      (data) => {
        for (let key in data.objects) {
          if (!Object.prototype.hasOwnProperty.call(data.objects, key)) continue;

          const geoJsonObject = topojson.feature(data, data.objects[key]);
          const geoLayer = L.geoJSON(geoJsonObject, {
            filter: (feature) =>
              !!(feature.properties?.NAMA_SUNGA || feature.properties?.SUNGAI),
            style: {
              color: "#4068D8",
              weight: 2,
              opacity: 1,
              fillColor: "#4068D8",
              fillOpacity: 1,
            },
            onEachFeature: (feature, layer) => {
              const riverName =
                feature.properties.NAMA_SUNGA || feature.properties.SUNGAI;
              layer.feature = layer.feature || feature;
              layer.on("mouseover", () => {
                const label = document.getElementById("das-label");
                if (riverName && label) {
                  label.textContent = "SUNGAI: " + riverName;
                  label.style.display = "block";
                  layer.setStyle({ weight: 3, fillOpacity: 0.8 });
                }
              });
              layer.on("mouseout", () => {
                const label = document.getElementById("das-label");
                if (label) label.style.display = "none";
                layer.setStyle({
                  color: "#4068D8",
                  weight: 2,
                  opacity: 1,
                  fillColor: "#4068D8",
                  fillOpacity: 1,
                });
              });
            },
          });

          geoLayer.addTo(layerGroup);
        }

        layerGroup.addTo(this.map);

        document
          .getElementById("toggle-river")
          ?.addEventListener("change", (e) => {
            this.toggleLayer(layerName, e.target.checked);
          });
      },
    );
  },

  async loadWatershedLayers() {
    try {
      const riverAreas = await DashboardAPI.get("/RiverArea/GetAllRiverArea");

      if (riverAreas.code === 200) {
        riverAreas.response.forEach((riverArea, index) => {
          if (riverArea.shpFile != null && riverArea.shpFile !== "") {
            this.loadWatershedFile(riverArea, index);
          }
        });
      }
    } catch (error) {
      console.error("Error loading watershed layers:", error);
    }
  },

  loadWatershedFile(riverArea, colorOffset = 0) {
    const colors = DashboardConfig.colors?.rivers || [
      "#e6194b",
      "#3cb44b",
      "#ffe119",
      "#4363d8",
      "#f58231",
      "#911eb4",
      "#46f0f0",
      "#f032e6",
      "#bcf60c",
      "#fabebe",
      "#008080",
      "#e6beff",
      "#9a6324",
      "#fffac8",
      "#800000",
      "#aaffc3",
      "#808000",
      "#ffd8b1",
      "#000075",
    ];

    const layerName = riverArea.shpFile.split(".")[0];
    const layerGroup = L.layerGroup();
    this.layers[layerName] = layerGroup;

    $.getJSON(
      `/data/watersheds/${riverArea.organizationCode}/${riverArea.shpFile}`,
      (data) => {
        let colorIndex = colorOffset;

        for (let key in data.objects) {
          if (!Object.prototype.hasOwnProperty.call(data.objects, key)) continue;

          const geoJsonObject = topojson.feature(data, data.objects[key]);
          const geoLayer = L.geoJSON(geoJsonObject, {
            style: (feature) => {
              const idx =
                feature.properties?._colorIndex ?? colorIndex % colors.length;
              return {
                fillColor: colors[idx % colors.length],
                fillOpacity: 0.2,
                color: "#fff",
                weight: 1,
                opacity: 0.5,
              };
            },
            onEachFeature: (feature, layer) => {
              const idx = colorIndex % colors.length;
              feature.properties = feature.properties || {};
              feature.properties._colorIndex = idx;
              feature.properties._areaName =
                feature.properties.NAMA_DAS || "Unknown";

              const baseStyle = {
                fillColor: colors[idx],
                fillOpacity: 0.2,
                color: "#fff",
                weight: 1,
                opacity: 0.5,
              };
              layer.setStyle(baseStyle);

              layer.on("mouseover", () => {
                const label = document.getElementById("das-label");
                const name = feature.properties._areaName;
                if (name && label) {
                  label.textContent = "DAS: " + name;
                  label.style.display = "block";
                  layer.setStyle({ weight: 2, fillOpacity: 0.5 });
                }
              });
              layer.on("mouseout", () => {
                const label = document.getElementById("das-label");
                if (label) label.style.display = "none";
                layer.setStyle(baseStyle);
              });

              colorIndex++;
            },
          });

          geoLayer.addTo(layerGroup);
        }

        layerGroup.addTo(this.map);

        document
          .getElementById("toggle-watershed")
          ?.addEventListener("change", (e) => {
            this.toggleLayer(layerName, e.target.checked);
          });
      },
    ).fail((error) => {
      console.error(
        `Error loading watershed file: ${riverArea.shpFile}`,
        error,
      );
    });
  },
};

window.changeMarker = (checkbox) => {
  MapModule.handleMarkerFilter(checkbox);
};
